import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowRight, BookOpen, Home, User, Filter, Download, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import ExitIntentPopup from '../components/ExitIntentPopup';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: 'all', label: 'All' },
  { key: 'glp1', label: 'GLP-1', match: ['ozempic', 'semaglutide', 'tirzepatide', 'wegovy', 'glp-1', 'glp1', 'plateau', 'appetite'] },
  { key: 'nad', label: 'NAD+', match: ['nad'] },
  { key: 'peptides', label: 'Peptides', match: ['peptide', 'bpc-157', 'bpc157', 'tb-500', 'tb500', 'wolverine', 'tesamorelin', 'sermorelin', 'motsc', 'mots-c', 'pt-141', 'pt141', 'thymosin'] },
  { key: 'womens', label: "Women's Health", match: ['women', 'hormone-glp1', 'estrogen', 'perimenopause', 'menopause', 'men-lose-weight-faster', 'nuvella'] },
  { key: 'mens', label: "Men's Health", match: ['apomorphine', 'directmax', 'men-over-50'] },
  { key: 'cognitive', label: 'Cognitive', match: ['cognitive', 'brain-fog', 'focus', 'mental-clarity'] },
  { key: 'sleep', label: 'Sleep', match: ['sleep', 'insomnia', 'tired'] },
  { key: 'stacks', label: 'Stacks', match: ['stack', 'wolverine'] },
  { key: 'antiaging', label: 'Anti-Aging', match: ['aging', 'anti-age', 'longevity', 'over-50'] },
];

const matchesCategory = (post, category) => {
  if (category.key === 'all') return true;
  const text = `${post.title} ${post.slug}`.toLowerCase();
  return category.match.some(keyword => text.includes(keyword));
};

const BlogIndex = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => { fetchPosts(); }, []);

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/posts`);
      if (!response.ok) throw new Error('Failed to fetch posts');
      const data = await response.json();
      setPosts(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const getReadTime = (wordCount) => {
    const minutes = Math.max(3, Math.round((wordCount || 0) / 200));
    return `${minutes} min read`;
  };

  const activeCat = CATEGORIES.find(c => c.key === activeFilter);
  const filteredPosts = useMemo(() =>
    posts.filter(p => matchesCategory(p, activeCat)),
    [posts, activeCat]
  );

  // Featured post: pinned slug, only for "all" or "nad" filter
  const showFeatured = activeFilter === 'all' || activeFilter === 'nad';
  const featuredPost = showFeatured ? posts.find(p => p.slug === 'nad-menopause-weight-gain') : null;
  const gridPosts = featuredPost
    ? filteredPosts.filter(p => (p.id || p.slug) !== (featuredPost.id || featuredPost.slug))
    : filteredPosts;

  return (
    <>
      <Helmet>
        <title>Weight Loss Insights & Articles | Bryan 2.0 Forecast</title>
        <meta name="description" content="Expert insights on medical weight loss, GLP-1 medications like Semaglutide and Tirzepatide, and achieving sustainable results." />
        <meta property="og:title" content="Weight Loss Insights & Articles | Bryan 2.0 Forecast" />
        <meta property="og:description" content="Expert insights on medical weight loss, GLP-1 medications, and achieving sustainable results." />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white" data-testid="blog-index-page">
        {/* Header */}
        <header className="border-b border-slate-800/50 backdrop-blur-xl bg-slate-950/80 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-emerald-500/20 border border-cyan-500/30 flex items-center justify-center group-hover:border-cyan-500/50 transition-all">
                <span className="text-lg font-bold bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">B</span>
              </div>
              <span className="font-heading text-xl font-bold text-white group-hover:text-cyan-300 transition-colors">Bryan 2.0</span>
            </Link>
            <nav className="flex items-center gap-6">
              <Link to="/" className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-2">
                <Home className="h-4 w-4" />
                <span className="hidden sm:inline">Home</span>
              </Link>
              <Link to="/blog" className="text-cyan-400 font-medium flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                <span className="hidden sm:inline">Blog</span>
              </Link>
              <Link to="/about" className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-2">
                <User className="h-4 w-4" />
                <span className="hidden sm:inline">About</span>
              </Link>
              <Link to="/free-starter-kit" className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-2">
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">Free Kit</span>
              </Link>
              <Link to="/optimize" className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-2">
                <Zap className="h-4 w-4" />
                <span className="hidden sm:inline">Optimize</span>
              </Link>
              <Button
                onClick={() => navigate('/quiz')}
                className="bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400 text-white rounded-full px-6 py-2 text-sm font-bold shadow-[0_0_20px_rgba(16,185,129,0.3)] hover:shadow-[0_0_30px_rgba(16,185,129,0.5)] transition-all"
                data-testid="blog-header-cta"
              >
                Get My Forecast
              </Button>
            </nav>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-16 sm:py-24 px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full border border-cyan-500/20 bg-cyan-500/5">
                <BookOpen className="h-4 w-4 text-cyan-400" />
                <span className="text-cyan-400 text-sm font-medium">INSIGHTS & ARTICLES</span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Weight Loss <span className="bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">Insights</span>
              </h1>
              <p className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto">
                Expert perspectives on medical weight loss, GLP-1 medications, and achieving sustainable results with Bryan 2.0.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Filter Bar */}
        <section className="px-4 sm:px-6 pb-10">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide" data-testid="blog-filter-bar">
              <Filter className="h-4 w-4 text-slate-500 flex-shrink-0" />
              {CATEGORIES.map(cat => (
                <button
                  key={cat.key}
                  onClick={() => setActiveFilter(cat.key)}
                  data-testid={`filter-${cat.key}`}
                  className={`flex-shrink-0 px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 border ${
                    activeFilter === cat.key
                      ? 'bg-gradient-to-r from-cyan-500 to-emerald-500 text-slate-950 border-transparent shadow-[0_0_16px_rgba(6,182,212,0.35)]'
                      : 'bg-slate-900/60 text-slate-300 border-slate-700 hover:border-cyan-500/40 hover:text-cyan-300'
                  }`}
                >
                  {cat.label}
                  {cat.key !== 'all' && (
                    <span className={`ml-1.5 text-xs ${activeFilter === cat.key ? 'text-slate-800' : 'text-slate-500'}`}>
                      {posts.filter(p => matchesCategory(p, cat)).length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Blog Content */}
        <section className="pb-24 px-4 sm:px-6">
          <div className="max-w-7xl mx-auto">
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="w-10 h-10 border-2 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin"></div>
              </div>
            ) : error ? (
              <div className="text-center py-20">
                <p className="text-red-400 mb-4">{error}</p>
                <Button onClick={fetchPosts} variant="outline" className="border-cyan-500/30 text-cyan-400">Try Again</Button>
              </div>
            ) : filteredPosts.length === 0 ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-slate-800/50 border border-slate-700 flex items-center justify-center">
                  <BookOpen className="h-10 w-10 text-slate-500" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">No Articles Found</h3>
                <p className="text-slate-400 mb-6">Try a different category filter.</p>
              </motion.div>
            ) : (
              <>
                {/* Featured Post */}
                {featuredPost && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mb-10"
                    data-testid="featured-post"
                  >
                    <Link to={`/blog/${featuredPost.slug}`}>
                      <div className="relative backdrop-blur-xl bg-slate-900/50 border border-cyan-500/20 rounded-2xl p-8 sm:p-10 lg:p-12 hover:border-cyan-500/40 hover:shadow-[0_0_40px_rgba(0,240,255,0.12)] transition-all duration-300 group overflow-hidden">
                        {/* Accent glow */}
                        <div className="absolute top-0 right-0 w-72 h-72 bg-gradient-to-bl from-cyan-500/8 to-transparent rounded-full blur-3xl pointer-events-none" />
                        <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-emerald-500/6 to-transparent rounded-full blur-3xl pointer-events-none" />

                        <div className="relative flex flex-col lg:flex-row lg:items-center lg:gap-12">
                          <div className="flex-1">
                            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20">
                              <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                              <span className="text-cyan-400 text-xs font-semibold uppercase tracking-wider">Featured</span>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                              <span className="flex items-center gap-1.5">
                                <Calendar className="h-4 w-4" />
                                {formatDate(featuredPost.published_at)}
                              </span>
                              <span className="flex items-center gap-1.5">
                                <Clock className="h-4 w-4" />
                                {getReadTime(featuredPost.word_count)}
                              </span>
                            </div>
                            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 group-hover:text-cyan-300 transition-colors leading-tight">
                              {featuredPost.title}
                            </h2>
                            <p className="text-slate-400 text-base sm:text-lg leading-relaxed mb-6 max-w-2xl">
                              {featuredPost.excerpt}
                            </p>
                            <div className="inline-flex items-center gap-2 text-cyan-400 font-semibold group-hover:text-cyan-300 transition-colors">
                              <span>Read Full Article</span>
                              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                )}

                {/* Post Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                  {gridPosts.map((post, index) => (
                    <motion.article
                      key={post.id || post.slug}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(index * 0.05, 0.5) }}
                      className="group"
                      data-testid={`blog-post-card-${post.slug}`}
                    >
                      <Link to={`/blog/${post.slug}`}>
                        <div className="h-full backdrop-blur-xl bg-slate-900/50 border border-slate-800 rounded-2xl p-6 sm:p-8 hover:border-cyan-500/30 hover:shadow-[0_0_30px_rgba(0,240,255,0.1)] transition-all duration-300">
                          {post.featured_image && (
                            <div className="aspect-video rounded-xl bg-slate-800/50 mb-6 overflow-hidden">
                              <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover" />
                            </div>
                          )}
                          <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                            <span className="flex items-center gap-1.5">
                              <Calendar className="h-4 w-4" />
                              {formatDate(post.published_at)}
                            </span>
                            <span className="flex items-center gap-1.5">
                              <Clock className="h-4 w-4" />
                              {getReadTime(post.word_count)}
                            </span>
                          </div>
                          <h2 className="font-heading text-xl sm:text-2xl font-bold text-white mb-3 group-hover:text-cyan-300 transition-colors leading-tight">
                            {post.title}
                          </h2>
                          <p className="text-slate-400 text-base leading-relaxed mb-6 line-clamp-3">
                            {post.excerpt}
                          </p>
                          <div className="flex items-center gap-2 text-cyan-400 font-medium group-hover:text-cyan-300 transition-colors">
                            <span>Read Article</span>
                            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </Link>
                    </motion.article>
                  ))}
                </div>
              </>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 sm:py-24 px-4 sm:px-6 border-t border-slate-800/50">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-4">Ready for Your Personalized Plan?</h2>
              <p className="text-slate-400 mb-8">Get your AI-powered weight loss forecast in under 2 minutes.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button
                  onClick={() => navigate('/quiz')}
                  className="bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400 text-white rounded-full px-10 py-6 text-lg font-bold shadow-[0_0_30px_rgba(16,185,129,0.4)] hover:shadow-[0_0_40px_rgba(16,185,129,0.6)] transition-all"
                  data-testid="blog-footer-cta"
                >
                  Get My AI Forecast
                </Button>
                <a
                  href="https://calendly.com/bryanm8623/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyan-400 hover:text-cyan-300 font-medium transition-colors"
                >
                  Or book a free call with Bryan
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-slate-800/50 py-8 px-4 sm:px-6">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
            <p>&copy; {new Date().getFullYear()} Bryan 2.0 Forecast. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <Link to="/" className="hover:text-cyan-400 transition-colors">Home</Link>
              <Link to="/blog" className="hover:text-cyan-400 transition-colors">Blog</Link>
              <Link to="/quiz" className="hover:text-cyan-400 transition-colors">Take Quiz</Link>
            </div>
          </div>
        </footer>
        <ExitIntentPopup />
      </div>
    </>
  );
};

export default BlogIndex;
